import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";

import BrandHeader from "@/components/onboarding/BrandHeader";
import ProgressIndicator from "@/components/onboarding/ProgressIndicator";
import Slide1Welcome from "@/components/onboarding/Slide1Welcome";
import Slide2Perks from "@/components/onboarding/Slide2Perks";
import Slide3Business from "@/components/onboarding/Slide3Business";
import Slide4Address from "@/components/onboarding/Slide4Address";
import Slide5Success from "@/components/onboarding/Slide5Success";

import { api } from "@/lib/api";

const STEP_LABELS = [
  "Welcome",
  "Founding Perks",
  "Business Details",
  "Address & Documents",
  "Confirmation",
];

const DEFAULT_BUSINESS = {
  brand_name: "",
  owner_name: "",
  mobile: "",
  whatsapp: "",
  email: "",
  product_category: "",
  product_categories: [],
  brand_story: "",
};

const DEFAULT_ADDRESS = {
  address_line_1: "",
  address_line_2: "",
  district: "",
  state: "",
  pin_code: "",
  gst_registered: false,
  gstin: "",
  pan_number: "",
};

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [business, setBusiness] = useState(DEFAULT_BUSINESS);
  const [address, setAddress] = useState(DEFAULT_ADDRESS);
  const [ownerName, setOwnerName] = useState("");
  const [resumeToken, setResumeToken] = useState(null);
  const containerRef = useRef(null);

  // Lenis — smooth momentum for inner scroll of the current slide when it overflows.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const lenis = new Lenis({
      wrapper: el,
      content: el.firstElementChild,
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  // Resume from ?resume=TOKEN
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("resume");
    if (!token) return;
    api
      .get(`/onboarding/resume/${token}`)
      .then((r) => {
        const d = r.data || {};
        if (d.email) {
          setBusiness((b) => ({ ...b, email: d.email, owner_name: d.owner_name || "" }));
          setOwnerName(d.owner_name || "");
        }
        if (d.business) setBusiness((b) => ({ ...b, ...d.business }));
        if (d.address) setAddress((a) => ({ ...a, ...d.address }));
        setResumeToken(token);
      })
      .catch(() => {
        /* silently ignore invalid token */
      });
  }, []);

  // Scroll container to top on step change
  useEffect(() => {
    containerRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [step]);

  const goto = (next) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };
  const handleNext = () => goto(step + 1);
  const handleBack = () => goto(Math.max(1, step - 1));

  const handleEmailSaved = (email, token) => {
    setBusiness((b) => (b.email ? b : { ...b, email }));
    setResumeToken(token);
  };

  const totalSteps = 5;
  const currentLabel = STEP_LABELS[step - 1];

  const slideAnim = useMemo(
    () => ({
      initial: { opacity: 0, y: direction > 0 ? 60 : -30 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: direction > 0 ? -30 : 30 },
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    }),
    [direction],
  );

  return (
    <div className="relative min-h-screen w-full bg-[color:var(--sbh-cream)] overflow-hidden">
      <div className="grain fixed inset-0 pointer-events-none z-[5]" />
      <BrandHeader />

      {/* Progress bar */}
      <div className="absolute top-[92px] md:top-[110px] left-5 md:left-10 lg:left-14 right-5 md:right-10 lg:right-14 z-30">
        <ProgressIndicator current={step} total={totalSteps} label={currentLabel} />
      </div>

      {/* Slide viewport */}
      <div
        ref={containerRef}
        className="relative w-full min-h-screen overflow-y-auto hide-scrollbar"
        data-testid="onboarding-viewport"
      >
        <div className="w-full max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pt-[140px] md:pt-[170px] pb-16 md:pb-24">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={step} {...slideAnim} data-testid={`slide-container-${step}`}>
              {step === 1 && (
                <Slide1Welcome
                  onNext={handleNext}
                  onEmailSaved={handleEmailSaved}
                  existingEmail={business.email}
                />
              )}
              {step === 2 && <Slide2Perks onNext={handleNext} onBack={handleBack} />}
              {step === 3 && (
                <Slide3Business
                  onNext={handleNext}
                  onBack={handleBack}
                  value={business}
                  onChange={setBusiness}
                  onEmailSaved={handleEmailSaved}
                  resumeToken={resumeToken}
                />
              )}
              {step === 4 && (
                <Slide4Address
                  onBack={handleBack}
                  business={business}
                  value={address}
                  onChange={setAddress}
                  resumeToken={resumeToken}
                  onSubmitted={(data) => {
                    setOwnerName(data.owner_name || business.owner_name);
                    goto(5);
                  }}
                />
              )}
              {step === 5 && <Slide5Success ownerName={ownerName || business.owner_name} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
